import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Trash2, Search, Mail, Phone } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const STATUSES = [
  { value: "new", label: "Nuevo" },
  { value: "contacted", label: "Contactado" },
  { value: "enrolled", label: "Inscrito" },
  { value: "discarded", label: "Descartado" },
];

export default function LeadsPanel({ onUnauthorized }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/leads");
      setLeads(data);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        onUnauthorized?.();
        return;
      }
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al cargar leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/leads/${id}`, { status });
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
    } catch (err) {
      toast.error("No se pudo actualizar");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este lead?")) return;
    try {
      await api.delete(`/leads/${id}`);
      setLeads((ls) => ls.filter((l) => l.id !== id));
      toast.success("Lead eliminado");
    } catch (err) {
      toast.error("No se pudo eliminar");
    }
  };

  const filtered = useMemo(() => {
    return leads.filter((l) => {
      if (filterStatus !== "all" && l.status !== filterStatus) return false;
      if (!q) return true;
      const s = q.toLowerCase();
      return (
        l.name.toLowerCase().includes(s) ||
        (l.email || "").toLowerCase().includes(s) ||
        (l.phone || "").toLowerCase().includes(s) ||
        (l.course || "").toLowerCase().includes(s) ||
        (l.branch || "").toLowerCase().includes(s)
      );
    });
  }, [leads, q, filterStatus]);

  const stats = useMemo(
    () => ({
      total: leads.length,
      new: leads.filter((l) => l.status === "new").length,
      contacted: leads.filter((l) => l.status === "contacted").length,
      enrolled: leads.filter((l) => l.status === "enrolled").length,
    }),
    [leads]
  );

  return (
    <div data-testid="leads-panel">
      <h2 className="font-display font-semibold tracking-tighter text-2xl md:text-3xl">
        Leads de contacto
      </h2>
      <p className="mt-1 text-sm text-[#525252]">
        Gestiona las solicitudes recibidas desde el sitio web.
      </p>

      {/* Stats */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-px bg-black/8 border border-black/8">
        {[
          { label: "Total", value: stats.total },
          { label: "Nuevos", value: stats.new },
          { label: "Contactados", value: stats.contacted },
          { label: "Inscritos", value: stats.enrolled },
        ].map((s) => (
          <div key={s.label} className="bg-white p-5">
            <div className="text-xs uppercase tracking-[0.18em] text-[#525252]">
              {s.label}
            </div>
            <div className="font-display font-bold text-3xl mt-2">{s.value}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="mt-6 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search
            size={16}
            strokeWidth={1.5}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500"
          />
          <input
            data-testid="admin-search"
            className="ie-input"
            style={{ paddingLeft: "2.5rem" }}
            placeholder="Buscar por nombre, email, teléfono…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <select
          data-testid="admin-filter-status"
          className="ie-input max-w-[200px]"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="all">Todos los estados</option>
          {STATUSES.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="mt-6 bg-white border border-black/8 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#fafafa] text-left">
              <tr className="text-[0.7rem] uppercase tracking-[0.18em] text-[#525252]">
                <th className="px-5 py-4">Nombre</th>
                <th className="px-5 py-4">Contacto</th>
                <th className="px-5 py-4">Tipo</th>
                <th className="px-5 py-4">Sucursal</th>
                <th className="px-5 py-4">Fecha</th>
                <th className="px-5 py-4">Estado</th>
                <th className="px-5 py-4" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-5 py-10 text-center text-[#525252]">
                    Cargando…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-5 py-10 text-center text-[#525252]">
                    No hay leads que coincidan.
                  </td>
                </tr>
              ) : (
                filtered.map((l) => (
                  <tr
                    key={l.id}
                    className="border-t border-black/5 hover:bg-[#fafafa]"
                    data-testid={`lead-row-${l.id}`}
                  >
                    <td className="px-5 py-4">
                      <div className="font-semibold">{l.name}</div>
                      {l.message && (
                        <div className="mt-1 text-xs text-[#525252] line-clamp-2 max-w-[280px]">
                          {l.message}
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex flex-col gap-1 text-xs">
                        {l.email && (
                          <a
                            href={`mailto:${l.email}`}
                            className="inline-flex items-center gap-2 text-[#0a0a0a] hover:text-[#c40000]"
                          >
                            <Mail size={12} strokeWidth={1.5} /> {l.email}
                          </a>
                        )}
                        <a
                          href={`https://wa.me/${l.phone.replace(/\D/g, "")}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-2 text-[#0a0a0a] hover:text-[#c40000]"
                        >
                          <Phone size={12} strokeWidth={1.5} /> {l.phone}
                        </a>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-xs">
                      {l.type === "visit" ? (
                        <div>
                          <span className="inline-block px-2 py-1 bg-[#c40000] text-white text-[0.6rem] uppercase tracking-[0.15em] font-semibold">
                            Visita
                          </span>
                          {l.visit_date && (
                            <div className="mt-1 text-[#525252]">
                              {new Date(l.visit_date + "T00:00:00").toLocaleDateString("es-MX", {
                                day: "numeric",
                                month: "short",
                              })}
                              {" · "}
                              {l.visit_time === "morning"
                                ? "Mañana"
                                : l.visit_time === "afternoon"
                                ? "Tarde"
                                : ""}
                            </div>
                          )}
                          {l.course && (
                            <div className="mt-1 text-[#525252]">{l.course}</div>
                          )}
                        </div>
                      ) : (
                        <div>
                          <span className="inline-block px-2 py-1 bg-[#0a0a0a] text-white text-[0.6rem] uppercase tracking-[0.15em] font-semibold">
                            Info
                          </span>
                          {l.course && (
                            <div className="mt-1 text-[#525252]">{l.course}</div>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-4 text-[#525252]">{l.branch || "—"}</td>
                    <td className="px-5 py-4 text-xs text-[#525252]">
                      {new Date(l.created_at).toLocaleString("es-MX")}
                    </td>
                    <td className="px-5 py-4">
                      <select
                        data-testid={`lead-status-${l.id}`}
                        value={l.status}
                        onChange={(e) => updateStatus(l.id, e.target.value)}
                        className="text-xs border border-black/15 px-2 py-1.5 bg-white"
                      >
                        {STATUSES.map((s) => (
                          <option key={s.value} value={s.value}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <button
                        data-testid={`lead-delete-${l.id}`}
                        onClick={() => remove(l.id)}
                        aria-label="Eliminar"
                        className="text-[#525252] hover:text-[#c40000]"
                      >
                        <Trash2 size={16} strokeWidth={1.5} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
